import type { AllocationResult, LabConfig, TestResult } from '../types';
import { loadWasmCpu } from '../wasm';
import { computeStats, formatBytes, measureFixedWork, now, safeAllocate } from '../utils';

const GROUP = 'C_memory';

function gcPressureSamples(): number[] {
  const samples: number[] = [];
  const garbage: object[] = [];
  for (let round = 0; round < 20; round++) {
    const t0 = now();
    for (let i = 0; i < 1000; i++) garbage.push({ index: i, data: new Array(100).fill(i) });
    garbage.length = 0;
    samples.push(now() - t0);
  }
  return samples;
}

async function wasmMemoryGrow(): Promise<{ maxPages: number; growTimesMs: number[] } | null> {
  if (typeof WebAssembly !== 'object') return null;
  try {
    const memory = new WebAssembly.Memory({ initial: 1, maximum: 4096, shared: false });
    const growTimes: number[] = [];
    let pages = 1;
    while (pages < 4096) {
      const t0 = now();
      try {
        memory.grow(Math.min(256, 4096 - pages));
        growTimes.push(now() - t0);
        pages += 256;
      } catch { break; }
    }
    return { maxPages: memory.buffer.byteLength / (64 * 1024), growTimesMs: growTimes };
  } catch { return null; }
}

export async function runMemoryTests(config: LabConfig): Promise<TestResult[]> {
  const t0 = now();
  const tests: TestResult[] = [];
  const isProd = config.mode === 'prod';

  let wasm: Awaited<ReturnType<typeof loadWasmCpu>> | null = null;
  try { wasm = await loadWasmCpu('/cpu.wasm'); } catch { /* tests below fail individually */ }

  // Max virtual allocation probe (research only; not used in prod).
  if (!isProd) {
    try {
      const sizesMb = [2048, 4096, 6144, 8192];
      const results: AllocationResult[] = [];
      for (const mb of sizesMb) {
        const r = safeAllocate(mb * 1024 * 1024, 500);
        results.push(r);
        if (!r.success || r.durationMs > 500) break;
      }
      const maxSuccessful = results.filter((r) => r.success).pop();
      tests.push({
        name: 'max_allocation',
        group: GROUP,
        success: true,
        durationMs: results.reduce((a, r) => a + r.durationMs, 0),
        metadata: {
          ladder: results.map((r) => ({ sizeMb: r.size / 1024 / 1024, success: r.success, durationMs: r.durationMs, error: r.error })),
          maxSuccessfulMb: maxSuccessful ? maxSuccessful.size / 1024 / 1024 : 0,
        },
      });
    } catch (err) {
      tests.push({ name: 'max_allocation', group: GROUP, success: false, durationMs: now() - t0, error: err instanceof Error ? err.message : String(err) });
    }
  }

  // Sequential memory bandwidth.
  try {
    if (!wasm) throw new Error('WASM not loaded');
    const sizes = config.memoryBandwidthSizes ?? [8, 64, 128];
    const results: Record<number, number[]> = {};
    for (const mb of sizes) {
      const bytes = mb * 1024 * 1024;
      const ptr = wasm.exports.__new(bytes, 4);
      wasm.exports.__pin(ptr);
      try {
        const view = new Uint8Array(wasm.memory.buffer, ptr, bytes);
        for (let i = 0; i < bytes; i += 4096) view[i] = 1; // pre-touch
        results[mb] = measureFixedWork(() => wasm!.runSequentialBandwidth(ptr, bytes), { runs: isProd ? 3 : 5, warmup: 1 });
      } finally {
        wasm.exports.__unpin(ptr);
      }
    }
    tests.push({
      name: 'memory_bandwidth',
      group: GROUP,
      success: true,
      durationMs: Object.values(results).flat().reduce((a, b) => a + b, 0),
      metadata: Object.fromEntries(
        Object.entries(results).map(([mb, samples]) => {
          const stats = computeStats(samples);
          const bytes = Number(mb) * 1024 * 1024;
          return [mb, { ...stats, gbPerSecond: (bytes / stats.median) * 1000 / (1024 ** 3) }];
        })
      ),
    });
  } catch (err) {
    tests.push({ name: 'memory_bandwidth', group: GROUP, success: false, durationMs: now() - t0, error: err instanceof Error ? err.message : String(err) });
  }

  // Pointer-chasing cache latency (JS; avoids AssemblyScript large-array limits).
  function buildChain(count: number): Int32Array {
    const next = new Int32Array(count);
    let seed = 123456789;
    const rand = () => { seed = (seed * 1664525 + 1013904223) >>> 0; return seed; };
    const indexes = new Int32Array(count);
    for (let i = 0; i < count; i++) indexes[i] = i;
    for (let i = count - 1; i > 0; i--) {
      const j = rand() % (i + 1);
      [indexes[i], indexes[j]] = [indexes[j], indexes[i]];
    }
    for (let i = 0; i < count; i++) next[indexes[i]] = indexes[(i + 1) % count];
    return next;
  }

  function chaseLatency(next: Int32Array, iterations: number): number {
    let idx = next[0];
    const t0 = now();
    for (let i = 0; i < iterations; i++) idx = next[idx];
    const t1 = now();
    if (idx < 0) throw new Error('unreachable');
    return (t1 - t0) / iterations;
  }

  try {
    const sizes = config.memoryLatencySizes ?? [16 * 1024, 64 * 1024, 256 * 1024, 1024 * 1024, 8 * 1024 * 1024];
    const results: Record<string, number[]> = {};
    for (const bytes of sizes) {
      const count = Math.floor(bytes / 4);
      const next = buildChain(count);
      const iterations = Math.min(2_000_000, Math.max(200_000, count * 16));
      const key = formatBytes(bytes);
      results[key] = measureFixedWork(() => chaseLatency(next, iterations), { runs: isProd ? 3 : 5, warmup: 1 });
    }
    tests.push({
      name: 'memory_latency',
      group: GROUP,
      success: true,
      durationMs: Object.values(results).flat().reduce((a, b) => a + b, 0),
      metadata: Object.fromEntries(Object.entries(results).map(([key, samples]) => [key, computeStats(samples)])),
    });
  } catch (err) {
    tests.push({ name: 'memory_latency', group: GROUP, success: false, durationMs: now() - t0, error: err instanceof Error ? err.message : String(err) });
  }

  // WASM memory grow (research only).
  if (!isProd && config.enableWASM) {
    try {
      const wasmResult = await wasmMemoryGrow();
      tests.push({
        name: 'wasm_memory_grow',
        group: GROUP,
        success: wasmResult !== null,
        durationMs: wasmResult ? wasmResult.growTimesMs.reduce((a, b) => a + b, 0) : 0,
        metadata: wasmResult ?? { error: 'WebAssembly not available' },
      });
    } catch (err) {
      tests.push({ name: 'wasm_memory_grow', group: GROUP, success: false, durationMs: now() - t0, error: err instanceof Error ? err.message : String(err) });
    }
  }

  // GC pressure (research only; too noisy for prod).
  if (!isProd) {
    try {
      const samples = gcPressureSamples();
      tests.push({
        name: 'gc_pressure',
        group: GROUP,
        success: true,
        durationMs: samples.reduce((a, b) => a + b, 0),
        samples,
        stats: computeStats(samples),
      });
    } catch (err) {
      tests.push({ name: 'gc_pressure', group: GROUP, success: false, durationMs: now() - t0, error: err instanceof Error ? err.message : String(err) });
    }
  }

  return tests;
}
